import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";

export type RecommendedMeal = {
  id: string | number;
  photoLabel: string;
  title: string;
  weight: string;
  ingredientsSummary: string;
  imageSrc: string;
  imageAlt?: string;
};

export type DogMealsGroup = {
  dogId: string;
  dogName: string;
  meals: RecommendedMeal[];
  hiddenAllergens?: string[];
};

type Props = {
  dogsData?: DogMealsGroup[];
  dogName?: string;
  meals?: RecommendedMeal[];
  hiddenAllergens?: string[];
  initialActiveDogId?: string | null;
};

const CARD_WIDTH = {
  xs: "196px",
  md: "255px",
};

export default function MealsDogWillLove({
  dogsData,
  dogName,
  meals = [],
  hiddenAllergens = [],
  initialActiveDogId,
}: Props) {
  const groups: DogMealsGroup[] = useMemo(() => {
    if (dogsData && dogsData.length > 0) {
      return dogsData;
    }

    return [
      {
        dogId: "default",
        dogName: dogName || "your dog",
        meals,
        hiddenAllergens,
      },
    ];
  }, [dogsData, dogName, meals, hiddenAllergens]);

  const [selectedDogId, setSelectedDogId] = useState<string>(() => {
    if (
      initialActiveDogId &&
      groups.some((group) => group.dogId === initialActiveDogId)
    ) {
      return initialActiveDogId;
    }

    return groups[0]?.dogId || "";
  });

  useEffect(() => {
    if (
      initialActiveDogId &&
      groups.some((group) => group.dogId === initialActiveDogId)
    ) {
      setSelectedDogId(initialActiveDogId);
    }
  }, [initialActiveDogId, groups]);

  const currentGroup =
    groups.find((group) => group.dogId === selectedDogId) || groups[0];

  if (!currentGroup) {
    return null;
  }

  const name = currentGroup.dogName?.trim() || "your dog";

  /*
   * Clean allergy values here as an extra safeguard.
   *
   * "none_option" is the stored value for "no allergies",
   * so it must NOT be displayed as an allergen.
   */
  const currentAllergens = (currentGroup.hiddenAllergens || [])
    .map((allergen) => String(allergen).trim().toLowerCase())
    .filter(
      (allergen) =>
        allergen &&
        allergen !== "none" &&
        allergen !== "none_option" &&
        allergen !== "none of these" &&
        allergen !== "no allergies"
    );

  const hasAllergies = currentAllergens.length > 0;

  const allergenText =
    currentAllergens.length === 1
      ? currentAllergens[0]
      : currentAllergens.length > 1
        ? `${currentAllergens.slice(0, -1).join(", ")} and ${
            currentAllergens[currentAllergens.length - 1]
          }`
        : "";

  const currentMeals = currentGroup.meals;

  return (
    <Box
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        ml: "-50vw",
        mr: "-50vw",

        bgcolor: QUIZ.color.cream2,

        py: {
          xs: "36px",
          md: "52px",
        },

        overflow: "hidden",
      }}
    >
      {/* MAIN CONTENT */}
      <Box
        sx={{
          width: "100%",

          maxWidth: {
            xs: "100%",
            sm: 520,
            md: 1180,
          },

          mx: "auto",
        }}
      >
        {/* =========================================
            TOP CONTENT
        ========================================== */}
        <Box
          sx={{
            px: {
              xs: "24px",
              md: "36px",
            },
          }}
        >
          {/* NEXT STEP */}
          <Typography
            sx={{
              fontFamily: QUIZ.font.button,
              fontWeight: 700,

              fontSize: {
                xs: 12,
                md: 14,
              },

              letterSpacing: "1.5px",
              textAlign: "center",
              color: QUIZ.color.brown,

              mb: 1,
            }}
          >
            NEXT STEP
          </Typography>

          {/* DOG SELECTOR */}
          {groups.length > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",

                gap: "10px",
                flexWrap: "wrap",

                mt: 1.5,

                mb: {
                  xs: 2,
                  md: 2.5,
                },
              }}
            >
              {groups.map((group) => {
                const isSelected =
                  group.dogId === currentGroup.dogId;

                return (
                  <Box
                    key={group.dogId}
                    component="button"
                    type="button"
                    onClick={() => setSelectedDogId(group.dogId)}
                    sx={{
                      px: {
                        xs: "16px",
                        md: "22px",
                      },

                      py: {
                        xs: "7px",
                        md: "9px",
                      },

                      borderRadius: QUIZ.radius.pill,

                      border: `2px solid ${
                        isSelected
                          ? QUIZ.color.brown
                          : QUIZ.color.cream3
                      }`,

                      bgcolor: isSelected
                        ? QUIZ.color.brown
                        : QUIZ.color.warmWhite,

                      color: isSelected
                        ? QUIZ.color.cream
                        : QUIZ.color.brown,

                      fontFamily: QUIZ.font.button,
                      fontWeight: 700,

                      fontSize: {
                        xs: 13,
                        md: 14,
                      },

                      cursor: "pointer",

                      display: "inline-flex",
                      alignItems: "center",

                      gap: "6px",

                      transition: "all 0.2s ease",

                      boxShadow: isSelected
                        ? "0 2px 8px rgba(74,48,38,0.18)"
                        : "none",

                      "&:hover": {
                        bgcolor: isSelected
                          ? QUIZ.color.brown
                          : QUIZ.color.cream2,
                      },
                    }}
                  >
                    <span>🐾</span>
                    <span>{group.dogName}</span>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* HEADING */}
          <Typography
            component="h2"
            sx={{
              fontFamily: QUIZ.font.display,
              fontWeight: 900,

              textTransform: "uppercase",

              textAlign: "center",

              color: QUIZ.color.brown,

              fontSize: {
                xs: 32,
                md: 40,
              },

              lineHeight: 1.08,

              mb: {
                xs: 3,
                md: 4,
              },
            }}
          >
            The meals {name} will love
          </Typography>

          {/* ALLERGY / ALL RECIPES MESSAGE */}
          <Box
            sx={{
              bgcolor: "#D7F0A2",

              borderRadius: {
                xs: "14px",
                md: "16px",
              },

              boxSizing: "border-box",

              width: {
                xs: "100%",
                md: "fit-content",
              },

              maxWidth: "100%",

              mx: {
                xs: 0,
                md: "auto",
              },

              px: {
                xs: "18px",
                md: "22px",
              },

              py: {
                xs: "18px",
                md: "15px",
              },

              mb: {
                xs: 3,
                md: 4,
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,

                fontSize: {
                  xs: 14,
                  md: 15,
                },

                lineHeight: {
                  xs: 1.5,
                  md: 1.4,
                },

                color: QUIZ.color.brown,

                textAlign: "center",

                whiteSpace: {
                  xs: "normal",
                  md: "nowrap",
                },
              }}
            >
              {hasAllergies ? (
                <>
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    We&apos;ve hidden anything with{" "}
                    {allergenText}
                  </Box>
                  , because you told us {name} is allergic to it.
                </>
              ) : (
                <>
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Great news!
                  </Box>{" "}
                  {name} can enjoy all of our recipes.
                </>
              )}
            </Typography>
          </Box>
        </Box>

        {/* =========================================
            MOBILE/DESKTOP CARDS OUTER GUTTER
        ========================================== */}
        <Box
          sx={{
            px: {
              xs: "24px",
              md: "36px",
            },

            boxSizing: "border-box",
          }}
        >
          {/* =======================================
              SCROLL VIEWPORT
          ======================================== */}
          <Box
            key={selectedDogId}
            sx={{
              width: "100%",

              overflowX: "auto",
              overflowY: "hidden",

              WebkitOverflowScrolling: "touch",

              scrollbarWidth: "none",

              "&::-webkit-scrollbar": {
                display: "none",
              },

              pb: "8px",
            }}
          >
            {/* CARDS TRACK */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",

                gap: {
                  xs: "16px",
                  md: "24px",
                },

                width: "max-content",

                mx: {
                  xs: 0,
                  md: "auto",
                },
              }}
            >
              {currentMeals.map((meal) => (
                <Box
                  key={meal.id}
                  sx={{
                    flex: "0 0 auto",

                    width: {
                      xs: CARD_WIDTH.xs,
                      md: CARD_WIDTH.md,
                    },

                    minWidth: {
                      xs: CARD_WIDTH.xs,
                      md: CARD_WIDTH.md,
                    },

                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* IMAGE */}
                  <Box
                    sx={{
                      position: "relative",

                      width: "100%",

                      aspectRatio: "1 / 1",

                      borderRadius: "14px",

                      overflow: "hidden",

                      bgcolor: QUIZ.color.cream3,
                    }}
                  >
                    <Box
                      component="img"
                      src={meal.imageSrc}
                      alt={meal.imageAlt ?? meal.title}
                      sx={{
                        width: "100%",
                        height: "100%",

                        objectFit: "cover",

                        display: "block",
                      }}
                    />
                  </Box>

                  {/* TITLE */}
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.display,

                      fontWeight: 900,

                      textTransform: "uppercase",

                      color: QUIZ.color.brown,

                      fontSize: {
                        xs: 14,
                        md: 17,
                      },

                      lineHeight: 1.12,

                      mt: {
                        xs: "10px",
                        md: "12px",
                      },
                    }}
                  >
                    {meal.title}
                  </Typography>

                  {/* DETAILS */}
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.body,

                      fontSize: {
                        xs: 13,
                        md: 14,
                      },

                      lineHeight: 1.4,

                      color: QUIZ.color.brown,

                      opacity: 0.7,

                      mt: "5px",
                    }}
                  >
                    {meal.weight} &middot;{" "}
                    {meal.ingredientsSummary}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* =========================================
            FOOTER
        ========================================== */}
        <Box
          sx={{
            px: {
              xs: "24px",
              md: "36px",
            },

            display: "flex",

            justifyContent: {
              xs: "flex-start",
              md: "center",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: QUIZ.font.body,

              fontWeight: 700,

              fontSize: {
                xs: 14,
                md: 15,
              },

              color: QUIZ.color.brown,

              mt: {
                xs: 2.5,
                md: 3,
              },

              textAlign: {
                xs: "left",
                md: "center",
              },
            }}
          >
            You&apos;ll pick {name}&apos;s favourites at the next step.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}